/**
 * Admin Preview — Direction 3 (Tradie Brutalist)
 *
 * Doubles down on the construction-site aesthetic. Space Mono for
 * eyebrows + headings, Satoshi for body. 2px black borders, hard 4px
 * offset shadows, danger stripes, asymmetric layouts. Cards translate
 * on hover so it feels physical.
 *
 * Self-contained mockup with static data. Reach it at /admin-preview-3.
 */
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Inbox as InboxIcon, MessageSquare,
  Clock, CalendarDays, Banknote, Users, UserCog, ShieldCheck,
  ShieldAlert, ClipboardCheck, Wrench, FileCheck2, ClipboardList,
  Hammer, FileSignature, Calendar, FolderKanban, Trophy, Heart,
  FileText, Receipt, Library, Truck, MapPin, Package, Boxes,
  Palette, CreditCard, HelpCircle, LifeBuoy, Sparkles, Phone,
  ChevronRight, Search, Bell, ArrowUpRight, ArrowLeft,
  AlertTriangle, Sun, Check, Building2,
} from "lucide-react";

// --- design tokens ---
const COL = {
  bg: "#F3F4F6",
  surface: "#FFFFFF",
  brand: "#FF5F15",
  ink: "#000000",
  text: "#0A0A0A",
  textMuted: "#4B5563",
  textDim: "#9CA3AF",
};

const fontHead = { fontFamily: "'Space Mono', ui-monospace, monospace", fontWeight: 700, letterSpacing: "-0.01em" };
const fontEyebrow = { fontFamily: "'Space Mono', ui-monospace, monospace", fontWeight: 400, letterSpacing: "0.16em" };
const fontBody = { fontFamily: "'Satoshi', system-ui, sans-serif" };

const NAV_TOP = [
  { label: "Overview", icon: LayoutDashboard, active: true },
  { label: "Inbox", icon: InboxIcon, badge: 4 },
  { label: "Chat", icon: MessageSquare, badge: 12 },
];

const NAV_GROUPS = [
  { id: "people", label: "People", items: [
    { label: "Timesheets", icon: Clock },
    { label: "Leave", icon: CalendarDays },
    { label: "Payroll", icon: Banknote },
    { label: "Team", icon: Users },
    { label: "Competencies", icon: UserCog },
  ]},
  { id: "safety", label: "Safety", items: [
    { label: "Safety", icon: ShieldCheck },
    { label: "Toolbox", icon: ClipboardCheck },
    { label: "Pre-Starts", icon: Wrench },
    { label: "SSSPs", icon: FileCheck2 },
    { label: "Forms", icon: ClipboardList },
  ]},
  { id: "work", label: "Work", items: [
    { label: "Jobs", icon: Hammer },
    { label: "Variations", icon: FileSignature },
    { label: "Dispatch", icon: Calendar },
    { label: "Clients", icon: Users },
    { label: "Suppliers", icon: Phone },
  ]},
];

// Brutalist hard-shadow card: relative positioning + a black sibling div
// offset 4px down/right with translate-on-hover.
const HARD = {
  card: "relative bg-white border-2 border-black transition-transform duration-150 hover:-translate-y-0.5 hover:translate-x-0.5",
  shadow: "absolute inset-0 -z-10 translate-x-1 translate-y-1 bg-black",
};

// Diagonal danger-stripe SVG pattern (CSS background).
const DANGER = {
  backgroundImage:
    "repeating-linear-gradient(45deg, #000 0 8px, transparent 8px 16px, #FF5F15 16px 24px, transparent 24px 32px)",
};

export default function AdminPreview3() {
  return (
    <div
      className="min-h-screen flex"
      style={{ ...fontBody, background: COL.bg, color: COL.text }}
      data-testid="admin-preview-3"
    >
      {/* ============================ SIDEBAR ============================ */}
      <aside
        className="w-64 shrink-0 border-r-2 flex flex-col"
        style={{ borderColor: COL.ink, background: COL.surface }}
      >
        {/* OVERSIZED WORDMARK */}
        <div className="border-b-2 px-4 py-5" style={{ borderColor: COL.ink }}>
          <div className="text-[10px]" style={{ ...fontEyebrow, color: COL.textDim }}>// CIVIL OS</div>
          <div className="text-2xl mt-1" style={fontHead}>
            FIELDLINK<span style={{ color: COL.brand }}>NZ</span>
          </div>
        </div>

        {/* TOP ITEMS */}
        <div className="border-b-2" style={{ borderColor: COL.ink }}>
          {NAV_TOP.map((it, i) => (
            <NavItem key={i} {...it} />
          ))}
        </div>

        {/* GROUPS */}
        <nav className="flex-1 overflow-y-auto">
          {NAV_GROUPS.map((g) => (
            <div key={g.id} className="border-b-2" style={{ borderColor: COL.ink }}>
              <div
                className="px-4 py-2 text-[10px] uppercase border-b"
                style={{ ...fontEyebrow, color: COL.textMuted, borderColor: COL.ink, background: "#FAFAFA" }}
              >
                // {g.label}
              </div>
              {g.items.map((it, i) => (
                <NavItem key={i} {...it} />
              ))}
            </div>
          ))}
          <div className="px-4 py-3 text-[10px]" style={{ ...fontEyebrow, color: COL.textDim }}>
            + 4 more groups…
          </div>
        </nav>

        {/* USER */}
        <div className="border-t-2 p-3 flex items-center gap-3" style={{ borderColor: COL.ink, background: COL.ink, color: "#fff" }}>
          <div
            className="w-9 h-9 flex items-center justify-center text-xs"
            style={{ background: COL.brand, color: "#000", ...fontHead }}
          >
            OM
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12px]" style={fontHead}>OFFICE MGR</div>
            <div className="text-[10px]" style={{ ...fontEyebrow, color: "#999" }}>OFFICE@FIELDLINK.NZ</div>
          </div>
        </div>
      </aside>

      {/* ============================ MAIN ============================ */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* DIAGONAL DANGER STRIPE */}
        <div className="h-2 w-full" style={DANGER} />

        {/* HEADER */}
        <header
          className="border-b-2 flex items-center justify-between px-6 py-3"
          style={{ borderColor: COL.ink, background: COL.surface }}
        >
          <div className="flex items-center gap-3">
            <Link
              to="/app"
              className="flex items-center gap-1.5 px-2 py-1 border-2 text-[11px]"
              style={{ borderColor: COL.ink, ...fontEyebrow }}
              data-testid="preview-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              BACK
            </Link>
            <span className="text-[11px]" style={{ ...fontEyebrow, color: COL.textMuted }}>
              MOCKUP · DIRECTION 3 · BRUTALIST
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: COL.ink }} />
              <input
                placeholder="SEARCH…"
                className="pl-8 pr-3 py-1.5 text-[12px] border-2 outline-none w-64 uppercase"
                style={{ borderColor: COL.ink, background: COL.surface, ...fontEyebrow }}
              />
            </div>
            <button className="p-2 border-2 relative" style={{ borderColor: COL.ink }}>
              <Bell className="w-4 h-4" />
              <span className="absolute -top-1 -right-1 w-3 h-3 border-2" style={{ background: COL.brand, borderColor: COL.ink }} />
            </button>
          </div>
        </header>

        {/* SCROLL */}
        <main className="flex-1 overflow-y-auto">
          <div className="px-8 py-8 max-w-7xl">
            {/* HERO STAMP */}
            <div className="mb-8">
              <div className="text-[11px] mb-3" style={{ ...fontEyebrow, color: COL.textMuted }}>
                // SHIFT // MON 22-02-2026 // 07:38 NZDT
              </div>
              <h1 className="text-6xl uppercase leading-[0.95]" style={fontHead}>
                MŌRENA,<br />
                <span style={{ color: COL.brand }}>OFFICE.</span>
              </h1>
              <p className="mt-4 max-w-md text-[14px] leading-relaxed">
                Six sites live. Two fleet flags. Tomorrow looks dry — push the Henderson pour.
              </p>
            </div>

            {/* SETUP STAMP CARD */}
            <SetupStamp />

            {/* STAT TILES */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatStamp label="TS PENDING" value="7" hint="↘ 2 VS LAST MON" rotate="-1deg" />
              <StatStamp label="HRS THIS WK" value="284" suffix="HRS" hint="↗ ON PACE" rotate="0.5deg" />
              <StatStamp label="JOBS LIVE" value="12" hint="3 STARTING TMRW" rotate="-0.5deg" />
              <StatStamp label="FLEET FLAGS" value="2" hint="WOF DUE" rotate="1deg" warn />
            </div>

            {/* BENTO — ASYMMETRIC */}
            <div className="mt-10 grid grid-cols-12 gap-6">
              <LiveFeed className="col-span-12 lg:col-span-7" />
              <div className="col-span-12 lg:col-span-5 space-y-6">
                <WeatherStamp />
                <RunSheetCard />
              </div>
              <FleetAlertsBox className="col-span-12" />
            </div>

            {/* FOOTER STAMP */}
            <div className="mt-12 border-t-2 pt-6" style={{ borderColor: COL.ink }}>
              <div className="text-[10px]" style={{ ...fontEyebrow, color: COL.textDim }}>
                // FIELDLINK NZ · TIER-1 READY · BUILT IN NZ · v1.0
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

// ============================================================
function NavItem({ icon: Icon, label, active, badge }) {
  return (
    <button
      type="button"
      className="w-full flex items-center gap-3 px-4 py-2.5 border-b text-left transition-colors"
      style={{
        borderColor: "#E5E7EB",
        background: active ? COL.ink : "transparent",
        color: active ? "#fff" : COL.text,
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = "#FAFAFA";
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = "transparent";
      }}
    >
      {Icon && <Icon className="w-4 h-4" strokeWidth={2} />}
      <span className="flex-1 text-[13px] uppercase" style={{ ...fontHead, fontWeight: active ? 700 : 400 }}>
        {label}
      </span>
      {badge != null && (
        <span
          className="text-[10px] px-1.5 py-0.5"
          style={{
            background: active ? COL.brand : COL.ink,
            color: active ? "#000" : "#fff",
            ...fontHead,
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

function HardCard({ children, className = "", style = {} }) {
  return (
    <div className="relative">
      <div className={`${HARD.card} ${className}`} style={style}>
        {children}
      </div>
      <div className={HARD.shadow} />
    </div>
  );
}

function SetupStamp() {
  const done = 6, total = 7;
  const pct = (done / total) * 100;
  const items = [
    { label: "Upload company logo", done: false },
    { label: "Set pay-week ending day", done: true },
    { label: "Invite your team", done: true },
    { label: "Add your first client", done: true },
    { label: "Create your first job", done: true },
    { label: "Add a fleet vehicle", done: true },
    { label: "Add GST + bank details", done: true },
  ];
  return (
    <HardCard>
      <div className="p-6">
        {/* stamp header */}
        <div className="flex items-start justify-between gap-6">
          <div>
            <div
              className="inline-block px-3 py-1 mb-3 border-2"
              style={{ borderColor: COL.ink, background: COL.brand, color: COL.ink, ...fontEyebrow, fontSize: 10 }}
            >
              ✦ FIRST WEEK SETUP ✦
            </div>
            <div className="text-3xl uppercase leading-tight" style={fontHead}>
              GET FIELDLINK<br />DIALLED IN.
            </div>
            <p className="mt-3 max-w-md text-[13px]" style={{ color: COL.textMuted }}>
              A few one-off things to set up so the crew can hit the ground running.
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px]" style={{ ...fontEyebrow, color: COL.textDim }}>// PROGRESS</div>
            <div className="text-7xl leading-none tabular-nums" style={fontHead}>
              {Math.round(pct)}<span style={{ color: COL.brand }}>%</span>
            </div>
            <div className="text-[11px]" style={{ ...fontEyebrow, color: COL.textMuted }}>
              {done} / {total} DONE
            </div>
          </div>
        </div>

        {/* chunky progress */}
        <div className="mt-6 border-2 h-3" style={{ borderColor: COL.ink }}>
          <div className="h-full" style={{ width: `${pct}%`, background: COL.brand }} />
        </div>

        {/* items as ticket rows */}
        <ul className="mt-6 space-y-2">
          {items.map((it, i) => (
            <li
              key={i}
              className="flex items-center gap-3 px-3 py-2.5 border-2"
              style={{
                borderColor: it.done ? "#E5E7EB" : COL.ink,
                background: it.done ? "#FAFAFA" : "#FFF5EE",
              }}
            >
              <div
                className="w-5 h-5 border-2 flex items-center justify-center shrink-0"
                style={{
                  borderColor: COL.ink,
                  background: it.done ? COL.brand : "transparent",
                }}
              >
                {it.done && <Check className="w-3.5 h-3.5" style={{ color: COL.ink }} strokeWidth={3} />}
              </div>
              <div
                className="text-[12px] uppercase flex-1"
                style={{
                  ...fontHead,
                  fontWeight: it.done ? 400 : 700,
                  color: it.done ? COL.textDim : COL.ink,
                  textDecoration: it.done ? "line-through" : "none",
                }}
              >
                #{String(i + 1).padStart(2, "0")} · {it.label}
              </div>
              {!it.done && (
                <span
                  className="px-2 py-1 text-[10px] border-2"
                  style={{ borderColor: COL.ink, background: COL.ink, color: "#fff", ...fontEyebrow }}
                >
                  DO IT →
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </HardCard>
  );
}

function StatStamp({ label, value, suffix, hint, rotate = "0deg", warn }) {
  return (
    <div className="relative" style={{ transform: `rotate(${rotate})` }}>
      <div
        className="bg-white border-2 p-5 transition-transform duration-150 hover:-translate-y-0.5 hover:translate-x-0.5"
        style={{ borderColor: COL.ink }}
      >
        <div className="text-[10px]" style={{ ...fontEyebrow, color: COL.textMuted }}>
          // {label}
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <div
            className="text-6xl leading-none tabular-nums"
            style={{ ...fontHead, color: warn ? COL.brand : COL.ink }}
          >
            {value}
          </div>
          {suffix && (
            <div className="text-[11px]" style={{ ...fontEyebrow, color: COL.textDim }}>
              {suffix}
            </div>
          )}
        </div>
        <div className="mt-3 pt-3 border-t-2 text-[10px]" style={{ borderColor: COL.ink, ...fontEyebrow, color: warn ? COL.brand : COL.textMuted }}>
          {hint}
        </div>
      </div>
      <div className="absolute inset-0 -z-10 translate-x-1 translate-y-1 bg-black" />
    </div>
  );
}

function LiveFeed({ className = "" }) {
  const rows = [
    { who: "BEN", what: "SUBMITTED TIMESHEET · 8.5H", job: "BRITOMART KERB", time: "12M" },
    { who: "MARA", what: "LOGGED HAZARD · SLIP RISK", job: "HENDERSON SW", time: "1H", warn: true },
    { who: "TAMA", what: "PRE-START: ALL CLEAR", job: "PLANT SP-04", time: "2H" },
    { who: "JESS", what: "UPLOADED DAILY LOG + 4 PHOTOS", job: "PAKENHAM EXT.", time: "3H" },
    { who: "OFFICE", what: "APPROVED LEAVE · 3 DAYS", job: "BEN", time: "4H" },
  ];
  return (
    <HardCard className={className}>
      <div className="border-b-2 px-5 py-3 flex items-center justify-between" style={{ borderColor: COL.ink }}>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: COL.brand }} />
          <span className="text-[11px]" style={fontEyebrow}>// LIVE FEED</span>
        </div>
        <button className="text-[10px] flex items-center gap-1" style={fontEyebrow}>
          SEE ALL <ArrowUpRight className="w-3 h-3" />
        </button>
      </div>
      <ul>
        {rows.map((r, i) => (
          <li
            key={i}
            className="flex items-center gap-4 px-5 py-3 border-b transition-colors hover:bg-zinc-50"
            style={{ borderColor: "#E5E7EB" }}
          >
            <div
              className="w-9 h-9 border-2 flex items-center justify-center text-[10px] shrink-0"
              style={{ borderColor: COL.ink, background: r.warn ? COL.brand : "#FAFAFA", ...fontHead }}
            >
              {r.who.slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px]" style={fontHead}>
                {r.who} <span style={{ color: COL.textMuted, fontWeight: 400 }}>{r.what}</span>
              </div>
              <div className="text-[10px] mt-0.5" style={{ ...fontEyebrow, color: COL.textDim }}>
                // {r.job}
              </div>
            </div>
            <div className="text-[11px] tabular-nums shrink-0" style={fontEyebrow}>{r.time}</div>
          </li>
        ))}
      </ul>
    </HardCard>
  );
}

function WeatherStamp() {
  return (
    <HardCard>
      <div className="p-5">
        <div className="text-[10px]" style={{ ...fontEyebrow, color: COL.textMuted }}>// TOMORROW · AKL</div>
        <div className="mt-3 flex items-center gap-3">
          <div className="w-14 h-14 border-2 flex items-center justify-center" style={{ borderColor: COL.ink, background: COL.brand }}>
            <Sun className="w-7 h-7" style={{ color: COL.ink }} strokeWidth={2} />
          </div>
          <div>
            <div className="text-5xl leading-none tabular-nums" style={fontHead}>21°</div>
            <div className="text-[10px] mt-1" style={{ ...fontEyebrow, color: COL.textMuted }}>
              PARTLY CLOUDY
            </div>
          </div>
        </div>
        <div
          className="mt-4 px-3 py-2 text-[10px] border-2"
          style={{ borderColor: COL.ink, background: "#FFF5EE", ...fontEyebrow, color: COL.ink }}
        >
          ⚠ RAIN WED/THU · SHIFT HENDERSON POUR
        </div>
      </div>
    </HardCard>
  );
}

function RunSheetCard() {
  const crew = [
    { name: "BEN", job: "BRITOMART KERB", lead: true },
    { name: "MARA", job: "HENDERSON SW" },
    { name: "TAMA", job: "PAKENHAM EXT." },
    { name: "JESS", job: "HENDERSON SW" },
  ];
  return (
    <HardCard>
      <div className="border-b-2 px-5 py-3 flex items-center justify-between" style={{ borderColor: COL.ink }}>
        <div className="text-[11px]" style={fontEyebrow}>// TMRW RUN SHEET</div>
        <button className="text-[10px] flex items-center gap-1" style={fontEyebrow}>
          DISPATCH <ArrowUpRight className="w-3 h-3" />
        </button>
      </div>
      <ul>
        {crew.map((c, i) => (
          <li
            key={i}
            className="flex items-center gap-3 px-4 py-2.5 border-b"
            style={{ borderColor: "#E5E7EB" }}
          >
            <div
              className="w-8 h-8 border-2 flex items-center justify-center text-[10px] shrink-0"
              style={{
                borderColor: COL.ink,
                background: c.lead ? COL.brand : "#fff",
                ...fontHead,
              }}
            >
              {c.name.slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px]" style={fontHead}>{c.name}{c.lead && <span style={{ color: COL.brand }}> · LEAD</span>}</div>
              <div className="text-[10px]" style={{ ...fontEyebrow, color: COL.textDim }}>// {c.job}</div>
            </div>
          </li>
        ))}
      </ul>
    </HardCard>
  );
}

function FleetAlertsBox({ className = "" }) {
  const rows = [
    { rego: "ABC123", asset: "HILUX UTE", issue: "WOF IN 6 DAYS" },
    { rego: "TR4501", asset: "TIPPER TRUCK", issue: "SERVICE +247KM" },
    { rego: "EX-08", asset: "20T EXCAVATOR", issue: "NO PRE-START" },
  ];
  return (
    <HardCard className={className}>
      <div className="border-b-2" style={{ borderColor: COL.ink }}>
        <div className="h-2 w-full" style={DANGER} />
        <div className="px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-[11px]" style={fontEyebrow}>// FLEET · 3 TO ACTION</span>
          </div>
          <span
            className="px-2 py-0.5 text-[10px] border-2"
            style={{ borderColor: COL.ink, background: COL.brand, color: COL.ink, ...fontEyebrow }}
          >
            URGENT
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
        {rows.map((r, i) => (
          <div
            key={i}
            className="px-5 py-4 border-r-2 last:border-r-0"
            style={{ borderColor: COL.ink }}
          >
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              <div className="text-[16px] tabular-nums" style={fontHead}>{r.rego}</div>
            </div>
            <div className="text-[10px] mt-2" style={{ ...fontEyebrow, color: COL.textMuted }}>
              {r.asset}
            </div>
            <div className="mt-3 px-2 py-1 border-2 text-[10px] inline-block" style={{ borderColor: COL.ink, background: "#FFF5EE", ...fontEyebrow }}>
              {r.issue}
            </div>
          </div>
        ))}
      </div>
    </HardCard>
  );
}
